import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Breadcrumb, Button, Space, Flex } from 'antd';
import { showSuccess, showError } from '../../../components/errorHandler';
import { apiRequest } from '../../../service/apiRequest';
import { AddModule } from '../../../redux/slice/userSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchMasterData } from '../../../redux/slice/masterSlice';
import { Link, useOutletContext } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import { customThemes } from '../../../themeConfig';

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state: any) => state.user.user);

  const [todayStatus, setTodayStatus] = useState<boolean | null>(null);
  const [dashboadStaticData, setdashboadStaticData] = useState<any>({});

  // Get dark mode and theme config from Outlet Context
  const context = useOutletContext<any>() || {};
  const darkMode = context.darkMode || false;
  const baseTheme = darkMode ? customThemes.dark : customThemes.light;
  const themeStyles = { ...baseTheme, ...context.themeStyles };

  // Theme constants
  const customLayoutBg = darkMode ? '#0f172a' : '#f8fafc'; // Slate-900 vs Slate-50
  const customCardBg = darkMode ? '#1e293b' : '#ffffff';   // Slate-800 vs Pure White
  const customCardBorder = darkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(226, 232, 240, 0.8)'; // Slate-700 vs Slate-200
  const customTextMain = darkMode ? '#f8fafc' : '#0f172a';  // Slate-50 vs Slate-900
  const customTextSub = darkMode ? '#94a3b8' : '#64748b';   // Slate-400 vs Slate-500

  const getSidebarData = async () => {
    try {
      const getModule = await apiRequest({
        method: 'get',
        url: 'modules',
      });
      dispatch(AddModule(getModule));
    } catch (error) {
      showError('Failed to fetch dashboard data');
    }
  };

  const handleLogin = async () => {
    showSuccess('Login successfully!');
    setTodayStatus(true); // simulate login
  };

  const handleLogout = async () => {
    showSuccess('Logout successfully!');
    setTodayStatus(false); // simulate logout
  };

  const getDashboardData = async () => {
    try {
      const DashboardData = await apiRequest({
        method: 'get',
        url: 'dashboard',
      });
      setdashboadStaticData(DashboardData);
    } catch (error) {
      showError('Failed to get DashboardData');
    }
  };

  useEffect(() => {
    console.log('Dashboard mounted. User:', user);
    if (user) {
      dispatch(fetchMasterData());
    }
  }, [user, dispatch]);

  useEffect(() => {
    getDashboardData();
    getSidebarData();
  }, []);

  /* ================= INJECT PREMIUM MODERN DASHBOARD STYLES ================= */
  useEffect(() => {
    const styleId = "dashboard-handicraft-modern-style";
    let styleTag = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      @keyframes cardSlideIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .dashboard-stat-card {
        background: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        border-radius: 12px !important;
        padding: 20px 24px !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        height: 130px !important;
        position: relative !important;
        overflow: hidden !important;
        transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.8s cubic-bezier(0.25, 1, 0.5, 1), border-color 0.8s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.8s cubic-bezier(0.25, 1, 0.5, 1) !important;
        will-change: transform, box-shadow, border-color;
        cursor: pointer !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01), 0 1px 2px rgba(0, 0, 0, 0.005) !important;
        animation: cardSlideIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) both;
      }

      /* Modern luxury glass sheen sweep effect */
      .dashboard-stat-card::after {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          transparent 45%,
          ${darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.35)'} 50%,
          transparent 55%
        );
        transform: rotate(-45deg) translate(-75%, -75%);
        transition: transform 1.4s cubic-bezier(0.25, 1, 0.5, 1);
        pointer-events: none;
        z-index: 1;
      }

      .dashboard-stat-card:hover {
        transform: translateY(-6px) !important;
        border-color: var(--accent-color) !important;
        box-shadow: 0 22px 30px -8px ${darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(148, 163, 184, 0.15)'}, 0 8px 12px -6px ${darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(148, 163, 184, 0.15)'}, 0 0 18px var(--accent-glow) !important;
      }

      .dashboard-stat-card:hover::after {
        transform: rotate(-45deg) translate(75%, 75%);
      }

      .dashboard-stat-icon {
        width: 52px;
        height: 52px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        transition: all 0.8s cubic-bezier(0.25, 1, 0.5, 1) !important;
        z-index: 2;
      }

      .dashboard-stat-card:hover .dashboard-stat-icon {
        transform: scale(1.12) rotate(6deg) !important;
        box-shadow: 0 4px 12px var(--accent-glow) !important;
      }

      .dashboard-table-card {
        background: ${customCardBg} !important;
        border: 1px solid ${customCardBorder} !important;
        border-radius: 12px !important;
        padding: 24px !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01) !important;
        animation: cardSlideIn 0.9s cubic-bezier(0.25, 1, 0.5, 1) both;
      }

      .dashboard-table-title {
        color: ${customTextMain} !important;
        font-size: 18px !important;
        font-weight: 700 !important;
        margin-bottom: 20px !important;
        letter-spacing: -0.01em;
      }

      /* Premium Ant Table Overrides */
      .dashboard-table .ant-table {
        background: transparent !important;
        color: ${customTextMain} !important;
      }

      .dashboard-table .ant-table-thead > tr > th {
        background: ${darkMode ? '#1e293b' : '#f8fafc'} !important;
        color: ${customTextMain} !important;
        border-bottom: 1px solid ${customCardBorder} !important;
        font-weight: 600 !important;
        font-size: 13px !important;
      }

      .dashboard-table .ant-table-tbody > tr > td {
        border-bottom: 1px solid ${darkMode ? 'rgba(51, 65, 85, 0.25)' : 'rgba(226, 232, 240, 0.5)'} !important;
        color: ${customTextMain} !important;
        background: transparent !important;
        font-size: 13px !important;
      }

      .dashboard-table .ant-table-tbody > tr:hover > td {
        background: ${darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'} !important;
      }

      /* Pagination controls styling */
      .dashboard-table .ant-pagination-item, 
      .dashboard-table .ant-pagination-prev, 
      .dashboard-table .ant-pagination-next {
        background: ${customCardBg} !important;
        border-color: ${customCardBorder} !important;
      }

      .dashboard-table .ant-pagination-item-active {
        border-color: ${themeStyles.colorPrimary || '#1890ff'} !important;
      }

      .dashboard-table .ant-pagination-item a,
      .dashboard-table .ant-pagination-item-active a,
      .dashboard-table .ant-pagination-prev .ant-pagination-item-link,
      .dashboard-table .ant-pagination-next .ant-pagination-item-link {
        color: ${customTextMain} !important;
      }
    `;
  }, [customCardBg, customCardBorder, customTextMain, darkMode, themeStyles]);



  const statPresests = [
    {
      title: 'Total PI',
      val: dashboadStaticData.total_pi || 0,
      link: '/pi',
      icon: <Icons.FileTextOutlined />,
      color: '#3b82f6', // Blue
      glow: 'rgba(59, 130, 246, 0.18)',
      bg: 'rgba(59, 130, 246, 0.08)'
    },
    {
      title: 'Purchase',
      val: dashboadStaticData.total_purchase_orders || 0,
      hideVal: true,
      link: '/purchase-order-master',
      icon: <Icons.ShoppingCartOutlined />,
      color: '#f59e0b', // Amber / Gold
      glow: 'rgba(245, 158, 11, 0.18)',
      bg: 'rgba(245, 158, 11, 0.08)',
      subLinks: [
        { label: 'Timber purchase', link: '/purchase-order?type=timber' },
        { label: 'Other purchase', link: '/purchase-order-products?type=store' }
      ]
    },
    {
      title: 'Alerts',
      val: dashboadStaticData.total_alert || 0,
      link: '/#',
      icon: <Icons.BellOutlined />,
      color: '#ef4444', // Red/Rose Alert
      glow: 'rgba(239, 68, 68, 0.18)',
      bg: 'rgba(239, 68, 68, 0.08)'
    }
  ];

  return (
    <div style={{ padding: '32px 24px', minHeight: '90vh', backgroundColor: customLayoutBg, transition: 'background-color 0.3s ease' }}>

      {/* HEADER BANNER */}
      <div
        style={{
          display: 'flex',
          marginBottom: '36px',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
          borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
          paddingBottom: '28px'
        }}
      >
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            margin: '0 0 6px 0',
            color: customTextMain,
            letterSpacing: '-0.02em',
          }}>
            Operations Dashboard
          </h1>
          <p style={{ margin: 0, color: customTextSub, fontSize: '15px' }}>
            Real-time status overview, resource tracking, and operational metrics.
          </p>
        </div>

        <Flex gap="small">
          {todayStatus ? (
            <Button
              type="primary"
              danger
              onClick={handleLogout}
              style={{ height: '40px', borderRadius: '6px', fontWeight: '600' }}
            >
              Logout for Today
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleLogin}
              style={{ height: '40px', borderRadius: '6px', fontWeight: '600', backgroundColor: themeStyles.colorPrimary }}
            >
              Login for Today
            </Button>
          )}
        </Flex>
      </div>

      {/* DYNAMIC BENTO STATISTICS GRID */}
      <Row gutter={[20, 20]} style={{ marginBottom: '32px' }}>
        {statPresests.map((item: any, index) => {
          const hasSubLinks = !!item.subLinks;
          
          const cardContent = (
            <div
              className="dashboard-stat-card"
              style={{
                '--accent-color': item.color,
                '--accent-glow': item.glow,
                animationDelay: `${index * 50}ms`,
                cursor: hasSubLinks ? 'default' : 'pointer'
              } as React.CSSProperties}
            >
              {/* Top Row: Title & Icon */}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {hasSubLinks ? (
                  <Link to={item.link} style={{ textDecoration: 'none', display: 'block' }}>
                    <p style={{ margin: '0', color: customTextSub, fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {item.title}
                    </p>
                  </Link>
                ) : (
                  <p style={{ margin: '0', color: customTextSub, fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {item.title}
                  </p>
                )}

                {hasSubLinks ? (
                  <Link to={item.link} style={{ textDecoration: 'none' }}>
                    <div
                      className="dashboard-stat-icon"
                      style={{
                        backgroundColor: item.bg,
                        color: item.color
                      }}
                    >
                      {item.icon}
                    </div>
                  </Link>
                ) : (
                  <div
                    className="dashboard-stat-icon"
                    style={{
                      backgroundColor: item.bg,
                      color: item.color
                    }}
                  >
                    {item.icon}
                  </div>
                )}
              </div>

              {/* Bottom Row: Value or Sub Links */}
              {hasSubLinks && item.subLinks ? (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: '8px',
                  width: '100%',
                  zIndex: 2
                }}>
                  {item.subLinks.map((sub: any, sIdx: number) => (
                    <Link 
                      key={sIdx} 
                      to={sub.link} 
                      style={{ 
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px 4px',
                        borderRadius: '6px',
                        background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                        color: customTextMain,
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}`,
                        whiteSpace: 'nowrap'
                      }}
                      className="dashboard-sub-link"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = item.bg;
                        e.currentTarget.style.color = item.color;
                        e.currentTarget.style.borderColor = item.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.color = customTextMain;
                        e.currentTarget.style.borderColor = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)';
                      }}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '800', color: customTextMain, letterSpacing: '-0.02em', alignSelf: 'flex-start' }}>
                  {item.val}
                </h2>
              )}
            </div>
          );

          return (
            <Col xs={24} sm={12} md={6} key={item.title}>
              {hasSubLinks ? (
                cardContent
              ) : (
                <Link to={item.link} style={{ textDecoration: 'none' }}>
                  {cardContent}
                </Link>
              )}
            </Col>
          );
        })}
      </Row>

    </div>
  );
};

export default Dashboard;
